// components/CaseTasks.jsx
import React, { useState, useEffect } from "react";
import {
  CheckSquare,
  Square,
  Clock,
  AlertCircle,
  Calendar,
  User,
  Loader2,
  ChevronDown,
  ChevronUp,
  Tag,
  MoreVertical,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const CaseTasks = ({ caseId }) => {
  const { isDarkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");

  useEffect(() => {
    fetchTasks();
  }, [caseId, filterStatus, filterPriority]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};

      if (filterStatus !== "all") {
        params.status = filterStatus;
      }

      if (filterPriority !== "all") {
        params.priority = filterPriority;
      }

      const response = await apiService.getCaseTasks(caseId, params);
      setTasks(response.data || []);
    } catch (err) {
      console.error("Error fetching case tasks:", err);
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (taskId) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const formatDate = (date) => {
    if (!date) return "No due date";
    const d = new Date(date);
    const now = new Date();
    const diffMs = d - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""}`;
    } else if (diffDays === 0) {
      return "Due today";
    } else if (diffDays === 1) {
      return "Due tomorrow";
    } else if (diffDays <= 7) {
      return `Due in ${diffDays} days`;
    }

    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: `text-gray-400 ${isDarkMode ? "bg-gray-500/10" : "bg-gray-200"}`,
      in_progress: "text-blue-400 bg-blue-500/10",
      review: "text-yellow-400 bg-yellow-500/10",
      completed: "text-green-400 bg-green-500/10",
      blocked: "text-red-400 bg-red-500/10",
    };
    return colors[status] || colors.todo;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: `text-gray-400 ${isDarkMode ? "bg-gray-500/10" : "bg-gray-200"}`,
      medium: "text-yellow-400 bg-yellow-500/10",
      high: "text-orange-400 bg-orange-500/10",
      urgent: "text-red-400 bg-red-500/10",
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityIcon = (priority) => {
    if (priority === "urgent" || priority === "high") {
      return <AlertCircle className="w-4 h-4" />;
    }
    return null;
  };

  const getStatusIcon = (status) => {
    if (status === "completed") {
      return <CheckSquare className="w-5 h-5 text-green-400" />;
    }
    return (
      <Square
        className={`w-5 h-5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      />
    );
  };

  const isDueOrOverdue = (dueDate) => {
    if (!dueDate) return false;
    const now = new Date();
    const due = new Date(dueDate);
    return due <= now;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getUserDisplay = (task) => {
    return task.assignedUser || task.salesforceOwner;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2
          className={`w-8 h-8 ${isDarkMode ? "text-blue-500" : "text-blue-600"} animate-spin`}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        <AlertCircle className="w-12 h-12 mb-3 text-red-500" />
        <p>{error}</p>
        <button
          onClick={fetchTasks}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Status:
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-700 border-gray-300"} text-sm rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="review">Review</option>
            <option value="completed">Completed</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        {/* Priority Filter */}
        <div className="flex items-center gap-2">
          <span
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Priority:
          </span>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={`${isDarkMode ? "bg-gray-700 text-gray-300 border-gray-600" : "bg-white text-gray-700 border-gray-300"} text-sm rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div
          className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          <CheckSquare
            className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
          />
          <p>No tasks found</p>
          <p className="text-sm mt-1">
            {filterStatus !== "all" || filterPriority !== "all"
              ? "Try changing the filters"
              : "No tasks have been created for this case yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => {
            const isExpanded = expandedTasks.has(task.id);
            const user = getUserDisplay(task);
            const isOverdue =
              task.status !== "completed" && isDueOrOverdue(task.dueDate);

            return (
              <div
                key={task.id}
                className={`${isDarkMode ? "bg-[#0C0E12]" : "bg-white"} border rounded-lg p-4 transition-all ${
                  isOverdue
                    ? "border-red-500/50 hover:border-red-500"
                    : isDarkMode
                      ? "border-gray-700 hover:border-gray-600"
                      : "border-gray-200 hover:border-gray-300"
                }`}
              >
                {/* Task Header */}
                <div className="flex items-start gap-3">
                  {/* Status Icon */}
                  <button
                    onClick={() => {
                      // Add toggle completion logic here
                    }}
                    className="mt-1 flex-shrink-0 hover:scale-110 transition-transform"
                  >
                    {getStatusIcon(task.status)}
                  </button>

                  {/* Task Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title and Priority */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3
                        className={`${isDarkMode ? "text-white" : "text-gray-900"} font-medium ${
                          task.status === "completed"
                            ? "line-through text-gray-500"
                            : ""
                        }`}
                      >
                        {task.title}
                      </h3>
                      <button
                        className={`${isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Status and Priority Badges */}
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                          task.status,
                        )}`}
                      >
                        {task.status.replace("_", " ").toUpperCase()}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${getPriorityColor(
                          task.priority,
                        )}`}
                      >
                        {getPriorityIcon(task.priority)}
                        {task.priority.toUpperCase()}
                      </span>
                    </div>

                    {/* Description Preview */}
                    {task.description && (
                      <div className="mb-3">
                        <p
                          className={`${isDarkMode ? "text-gray-400" : "text-gray-600"} text-sm`}
                        >
                          {isExpanded
                            ? task.description
                            : task.description.substring(0, 150) +
                              (task.description.length > 150 ? "..." : "")}
                        </p>
                        {task.description.length > 150 && (
                          <button
                            onClick={() => toggleExpanded(task.id)}
                            className="text-blue-400 hover:text-blue-300 text-xs mt-1 flex items-center gap-1"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                Show More
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Task Metadata */}
                    <div
                      className={`flex flex-wrap items-center gap-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {/* Due Date */}
                      {task.dueDate && (
                        <div
                          className={`flex items-center gap-1 ${
                            isOverdue ? "text-red-400" : ""
                          }`}
                        >
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(task.dueDate)}</span>
                        </div>
                      )}

                      {/* Assigned User */}
                      {user && (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
                            {user.profileImageUrl ? (
                              <img
                                src={user.profileImageUrl}
                                alt={user.name}
                                className="w-6 h-6 rounded-full"
                              />
                            ) : (
                              getInitials(user.name)
                            )}
                          </div>
                          <span
                            className={
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }
                          >
                            {user.name}
                          </span>
                        </div>
                      )}

                      {/* Progress */}
                      {task.progress > 0 && (
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-20 h-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full overflow-hidden`}
                          >
                            <div
                              className="h-full bg-blue-500 rounded-full transition-all"
                              style={{ width: `${task.progress}%` }}
                            />
                          </div>
                          <span>{task.progress}%</span>
                        </div>
                      )}

                      {/* Hours */}
                      {(task.estimatedHours || task.actualHours) && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>
                            {task.actualHours || 0}h
                            {task.estimatedHours
                              ? ` / ${task.estimatedHours}h`
                              : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    {task.tags && task.tags.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Tag
                          className={`w-3 h-3 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                        />
                        {task.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className={`text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"} px-2 py-0.5 rounded`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Salesforce Info */}
                    {task.source === "salesforce" && (
                      <div className="mt-2 text-xs">
                        <span className="bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded">
                          Synced from Salesforce
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaseTasks;
